/**
 * Axios HTTP Client Implementation
 *
 * Single Responsibility: Handles HTTP communication using Axios
 * Liskov Substitution: Can be replaced with any IHttpClient implementation
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  IHttpClient,
  HttpRequestConfig,
  HttpResponse,
} from '../interfaces/IHttpClient';

export class AxiosHttpClient implements IHttpClient {
  private client: AxiosInstance;

  constructor(baseURL?: string, defaultHeaders?: Record<string, string>) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...defaultHeaders,
      },
      timeout: 30000, // 30 seconds default timeout
    });
  }

  async get<T>(
    url: string,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>> {
    const axiosConfig: AxiosRequestConfig = {
      headers: config?.headers,
      params: config?.params,
      timeout: config?.timeout,
      signal: config?.signal,
    };

    const response: AxiosResponse<T> = await this.client.get(url, axiosConfig);
    return this.transformResponse(response);
  }

  async post<T>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>> {
    const axiosConfig: AxiosRequestConfig = {
      headers: config?.headers,
      params: config?.params,
      timeout: config?.timeout,
      signal: config?.signal,
    };

    try {
      const response: AxiosResponse<T> = await this.client.post(
        url,
        data,
        axiosConfig
      );
      return this.transformResponse(response);
    } catch (error) {
      // Re-throw AxiosError as-is so repository can handle it properly
      throw error;
    }
  }

  async put<T>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>> {
    const axiosConfig: AxiosRequestConfig = {
      headers: config?.headers,
      params: config?.params,
      timeout: config?.timeout,
      signal: config?.signal,
    };

    const response: AxiosResponse<T> = await this.client.put(
      url,
      data,
      axiosConfig
    );
    return this.transformResponse(response);
  }

  async delete<T>(
    url: string,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>> {
    const axiosConfig: AxiosRequestConfig = {
      headers: config?.headers,
      params: config?.params,
      timeout: config?.timeout,
      signal: config?.signal,
    };

    const response: AxiosResponse<T> = await this.client.delete(
      url,
      axiosConfig
    );
    return this.transformResponse(response);
  }

  private transformResponse<T>(response: AxiosResponse<T>): HttpResponse<T> {
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers as Record<string, string>,
    };
  }
}
